/**
 * Visitor-session key for the SPA — same contract as akku-24/akkubolt
 * (utils/session.ts there): generated client-side, sent as X-SESSION-ID on
 * every /api call, registered once per browsing session.
 *
 * sessionStorage — not localStorage — is deliberate: it matches the lifetime
 * of a PHP browsing session, so "did this visit lead to an order?" stays the
 * question being answered. localStorage would merge unrelated visits weeks
 * apart into one session and silently inflate attribution.
 */

const STORAGE_KEY = 'asos_session_key'

function randomKey(): string {
	// crypto.randomUUID needs a secure context; every production origin is https, but keep
	// a fallback so a plain-http preview cannot throw and break the whole API client.
	try {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID()
		}
		if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
			const b = new Uint8Array(16)
			crypto.getRandomValues(b)
			return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
		}
	} catch {
		/* fall through */
	}
	return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`
}

/** Get-or-create the key for this browsing session. Safe without window. */
export function getSessionKey(): string | null {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) return null
		let key = window.sessionStorage.getItem(STORAGE_KEY)
		if (!key) {
			key = randomKey()
			window.sessionStorage.setItem(STORAGE_KEY, key)
		}
		return key
	} catch {
		// Private-mode / storage-disabled browsers: degrade to no session rather
		// than throwing. Tracking is a nice-to-have; the page must still render.
		return null
	}
}
